import React, { useState, useEffect } from 'react';
import { getRedirectResult, type UserCredential } from 'firebase/auth';
import {
  signUp,
  login,
  signInWithGoogle,
  updateUserLanguage,
  auth
} from '../utils/firebase';
import { useStore } from '../store/useStore';
import { translations } from '../utils/translations';
import { Home, X } from 'lucide-react';

interface AuthProps {
  initialMode?: boolean;
  onBack?: () => void;
  language?: 'en' | 'es';
}

const Auth: React.FC<AuthProps> = ({ initialMode = true, onBack, language: propLanguage = 'en' }) => {
  const { setUser, setLanguage: setStoreLanguage } = useStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLogin, setIsLogin] = useState(initialMode);
  const [error, setError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [language, setLocalLanguage] = useState<'en' | 'es'>(propLanguage);
  const [showDetailsForm, setShowDetailsForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);


  useEffect(() => {
    setStoreLanguage(language);
    localStorage.setItem('language', language);
  }, [language, setStoreLanguage]);

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError(translations[language].email);
      return;
    }

    if (!acceptedTerms) {
      setError(translations[language].acceptTerms);
      return;
    }

    setShowDetailsForm(true);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isLogin) {
        const user = await login(email, password);
        await updateUserLanguage(user.uid, language);
        await setUser(user);
        if (onBack) onBack();
      } else {
        if (!acceptedTerms) {
          setError(translations[language].acceptTerms);
          return;
        }
        const user = await signUp(email, password, displayName);
        await updateUserLanguage(user.uid, language);
        await setUser(user);
      }
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : translations[language].authenticationFailed
      );
    }
  };

  const handleGoogleSignIn = async () => {
    if (!isLogin && !acceptedTerms) {
      setError(translations[language].acceptTerms);
      return;
    }

    try {
      const user = await signInWithGoogle();
      if (user) {
        await updateUserLanguage(user.uid, language);
        await setUser(user);
        if (onBack) onBack();
      }
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : translations[language].googleSignInFailed
      );
    }
  };

  const handleHomeClick = () => {
    if (onBack) onBack();
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setEmail('');
    setPassword('');
    setDisplayName('');
    setAcceptedTerms(false);
    setShowDetailsForm(false);
    setShowDetails(false);
    setError('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg relative max-h-[90vh] flex flex-col">
        <div className="sticky top-0 z-10 bg-white rounded-t-lg border-b">
          <div className="flex justify-end p-2">
            <button
              onClick={handleHomeClick}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
              title="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={handleHomeClick}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
              title={translations[language].backToHome}
            >
              <Home className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-bold text-center flex-grow">
              {isLogin ? translations[language].login : translations[language].signUp}
            </h2>
            <div className="flex gap-2">
              <button
                onClick={() => setLocalLanguage('en')}
                className={`px-3 py-1.5 text-sm rounded-md uppercase ${
                  language === 'en'
                    ? 'bg-black text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLocalLanguage('es')}
                className={`px-3 py-1.5 text-sm rounded-md uppercase ${
                  language === 'es'
                    ? 'bg-black text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                ES
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
              {error}
            </div>
          )}

          <form onSubmit={isLogin ? handleSubmit : (!showDetailsForm ? handleContinue : handleSubmit)} className="space-y-4">
            <div className="space-y-4">
              {isLogin ? (
                <>
                  <p className="text-gray-600 mb-6">
                    Welcome back! Please enter your credentials.
                  </p>

                  <div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={translations[language].email}
                      className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={translations[language].password}
                      className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full p-2 text-white bg-blue-500 hover:bg-blue-600 rounded"
                  >
                    {translations[language].login}
                  </button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">
                        {translations[language].continueWith}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    className="w-full p-2 border border-gray-300 rounded flex items-center justify-center space-x-2 hover:bg-gray-50"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span>{translations[language].google}</span>
                  </button>
                </>
              ) : showDetailsForm ? (
                <>
                  <h3 className="text-lg font-medium mb-4">{translations[language].almostDone}</h3>
                  <div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={translations[language].password}
                      className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder={translations[language].name}
                      className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full p-2 text-white bg-blue-500 hover:bg-blue-600 rounded"
                  >
                    {translations[language].signUp}
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-6">
                    {translations[language].welcomeSignup}
                  </p>

                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    className={`w-full p-2 border border-gray-300 rounded flex items-center justify-center space-x-2 ${
                      acceptedTerms
                        ? 'hover:bg-gray-50'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                    disabled={!acceptedTerms}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span>{translations[language].google}</span>
                  </button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">
                        {translations[language].orCreateAccount}
                      </span>
                    </div>
                  </div>

                  <div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={translations[language].email}
                      className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="p-4 bg-gray-50 rounded">
                    <div className="mb-2">
                      <h3 className="font-medium">
                        {translations[language].importantInfo}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {translations[language].dataSecurityInfo}
                      </p>
                      <button
                        onClick={() => setShowDetails(!showDetails)}
                        className="text-sm text-blue-600 font-semibold hover:text-blue-700 mt-1"
                      >
                        {translations[language].seeDetails}
                      </button>
                    </div>
                    {showDetails && (
                      <ul className="list-none text-sm space-y-2 text-gray-600 mt-3 border-t pt-3">
                        {[
                          translations[language].secureApiKeys,
                          translations[language].secureHistory,
                          translations[language].permanentDeletion,
                          translations[language].noTraining
                        ].map((text, index) => {
                          const parts = text.split('|');
                          return (
                            <li key={index} className="flex items-start">
                              <span className="mr-2">-</span>
                              <span>
                                {parts.map((part, i) => (
                                  <span key={i} className={i % 2 === 0 ? 'font-semibold' : ''}>
                                    {part}
                                  </span>
                                ))}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    <div className="mt-4 flex items-center">
                      <input
                        type="checkbox"
                        id="accept-terms"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="mr-2"
                      />
                      <label htmlFor="accept-terms" className="text-sm">
                        {translations[language].acceptTerms}
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className={`w-full p-2 text-white rounded ${
                      !acceptedTerms
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                    disabled={!acceptedTerms}
                  >
                    {translations[language].continue}
                  </button>
                </>
              )}
            </div>
          </form>

          <button
            onClick={toggleMode}
            className="mt-4 w-full text-blue-500 hover:underline py-2"
          >
            {isLogin
              ? translations[language].needAccount
              : translations[language].alreadyHaveAccount}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Auth;